import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { useToast } from '../lib/useToast';
import { useEscToClose } from '../lib/useEscToClose';
import { VenuePicker } from '../components/VenuePicker';
import { venueDisplayName, type GrowthVenue } from '../lib/growth';
import { PageHeader } from '../ui/PageHeader';
import { Field } from '../ui/Field';
import { StatCard } from '../ui/StatCard';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonRows } from '../ui/Skeleton';

/**
 * PARTNERSHIPS — the offers, the table tents that carry them, and which venues belong together.
 *
 * Server side: `supabase/migrations/20260922000000_partnerships_v1.sql`, its companion
 * `…0001_partnerships_v1_functions.sql`, and `20260923020000_partner_offer_approval.sql`.
 * Nothing here writes a table directly: the partnership tables carry NO insert/update policy at
 * all, so every write goes through an `_assert_admin_caller()`-gated RPC. The only direct reads
 * are the two affiliation tables, which grant `authenticated` a plain `using (true)` select —
 * there is no admin list RPC for them, so that listing is a real table read.
 *
 * ⚠️ AN OFFER HAS TWO INDEPENDENT AXES AND CONFLATING THEM IS THE MISTAKE TO AVOID.
 *   · `status` — pending / approved / rejected. A venue can PROPOSE from its portal; only Evan
 *     approves. Anything Evan authors here is `approved` the moment he writes it.
 *   · `is_active` — published or not.
 * Approving is not publishing: `admin_review_partner_offer` takes `p_activate` as a SEPARATE
 * argument so a venue's wording can be accepted today and go live later. All three read gates
 * (the RLS policy, `partner_offer_for_tent`, `partner_reveal_offer`) require `status = 'approved'`
 * AND `is_active`, so flipping either one alone publishes nothing.
 *
 * ⚠️ TWO VERIFICATION MODES, AND `reveals` MEANS SOMETHING DIFFERENT IN EACH.
 *   · scan OFF (the default): no staff device exists, so the reveal IS the redemption. Nothing
 *     ever sets `redeemed_at`, so the `redemptions` count stays 0 forever — it is not a dropoff,
 *     there is simply nothing to burn the code. This screen therefore shows the funnel as
 *     Scans → Claimed and never draws a third step.
 *   · scan ON (venue opt-in): staff burn the code at the open /scan page, `redeemed_at` lands, and
 *     the third step becomes real.
 * One three-step funnel for both would read as a 100% dropoff on every default offer.
 */

/**
 * ⚠️ `v.joinshado.com`, NOT `go.joinshado.com` — corrected 2026-09-25 by Evan: *"for QR's i thought
 * we are just already using venue.joinshado.com."* He was right and the original choice was a real
 * error. `app.json` lists THREE associated domains — `venue.joinshado.com`, `v.joinshado.com` and
 * `go.joinshado.com` — and the first agent to touch this picked `go` because it appeared in that
 * list, **without checking that it resolves. It does not: `go` has no DNS record at all, while
 * `venue`, `v`, `portal` and `scan` all point at Vercel.** So the tents would have carried a QR to
 * a hostname that has never existed, on every build.
 *
 * `v` over `venue` because a QR's error-correction overhead grows with payload length and this
 * string is printed on card stock — the shorter host is the more scannable code, and both are
 * equally trusted by the binary.
 *
 * ⚠️ STILL NOT SERVED. `v.joinshado.com` resolves, but nothing answers `/t/<slug>`, and an
 * `apple-app-site-association` claiming `/t/*` has to be in place before iOS will hand the link to
 * the app. Until both exist, a scanned tent lands nowhere. **Do not print tents.**
 */
const TENT_URL_BASE = 'https://v.joinshado.com/t/';

const PLACEMENT_KINDS = [
  { value: 'table_tent', label: 'Table tent' },
  { value: 'poster', label: 'Poster' },
  { value: 'flyer', label: 'Flyer' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'window', label: 'Window' },
  { value: 'bar_top', label: 'Bar top' },
  { value: 'other', label: 'Other' },
];

function placementLabel(kind: string): string {
  return PLACEMENT_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

type OfferStatusValue = 'pending' | 'approved' | 'rejected';

type OfferRow = {
  id: string;
  venue_id: string;
  venue_name: string | null;
  title: string;
  detail: string | null;
  terms: string | null;
  status: OfferStatusValue;
  is_active: boolean;
  scan_verification_enabled: boolean;
  /** What a reader must do to take it: 'first_rating' | 'none'. Defaults to 'none' server-side. */
  earn_condition: string | null;
  starts_at: string | null;
  ends_at: string | null;
  max_redemptions: number | null;
  proposed_by: string | null;
  proposed_by_username: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
  reveals: number;
  redemptions: number;
  tent_scans: number;
  placements: number;
};

type TentRow = {
  tent_id: string;
  venue_id: string;
  venue_name: string | null;
  label: string;
  placement: string;
  slug: string;
  is_active: boolean;
  scans: number;
  reveals: number;
  redemptions: number;
  last_scan_at: string | null;
};

type AffiliationGroup = {
  id: string;
  name: string;
  note: string | null;
  created_at: string;
  members: { id: string; name: string }[];
};

type Badge = { label: string; tone: 'success' | 'warning' | 'neutral' | 'accent' | 'danger' };

/** Status and publication are two axes; this collapses them into the one word that matters most. */
function offerBadge(o: OfferRow): Badge {
  if (o.status === 'pending') return { label: 'Pending review', tone: 'warning' };
  if (o.status === 'rejected') return { label: 'Rejected', tone: 'danger' };
  if (!o.is_active) return { label: 'Approved · not live', tone: 'neutral' };
  const now = Date.now();
  if (o.starts_at && new Date(o.starts_at).getTime() > now) return { label: 'Scheduled', tone: 'accent' };
  if (o.ends_at && new Date(o.ends_at).getTime() < now) return { label: 'Ended', tone: 'neutral' };
  if (o.max_redemptions != null && o.reveals >= o.max_redemptions) return { label: 'Capped', tone: 'warning' };
  return { label: 'Live', tone: 'success' };
}

/** `2026-09-22T19:30` in the operator's own timezone — what `<input type="datetime-local">` wants. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function formatWindow(o: Pick<OfferRow, 'starts_at' | 'ends_at'>): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  if (!o.starts_at && !o.ends_at) return 'No end date';
  if (o.starts_at && o.ends_at) return `${fmt(o.starts_at)} → ${fmt(o.ends_at)}`;
  if (o.starts_at) return `From ${fmt(o.starts_at)}`;
  return `Until ${fmt(o.ends_at as string)}`;
}

function pct(numerator: number, denominator: number): string {
  if (denominator <= 0) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

/**
 * `VenuePicker` renders whatever `venueDisplayName` reads off the row, and nothing else, so an
 * offer being edited can hand it a two-field stand-in rather than forcing a second venue fetch.
 */
function venueStub(id: string, name: string | null): GrowthVenue {
  return {
    id,
    venue_name_user_input: name,
    osm_name: null,
    venue_type: null,
    venue_shado_score: null,
    venue_shado_unique_users: null,
    venue_shado_rating_count: null,
    fsq_popularity: null,
    city_id: null,
    locality: null,
    is_hidden: null,
  };
}

type OfferDraft = {
  id: string | null;
  status: OfferStatusValue;
  venueId: string | null;
  venueName: string | null;
  title: string;
  detail: string;
  terms: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  maxRedemptions: string;
};

const EMPTY_OFFER: OfferDraft = {
  id: null,
  status: 'approved',
  venueId: null,
  venueName: null,
  title: '',
  detail: '',
  terms: '',
  startsAt: '',
  endsAt: '',
  isActive: false,
  maxRedemptions: '',
};

type TentDraft = {
  id: string | null;
  offerId: string;
  label: string;
  placement: string;
  isActive: boolean;
};

const EMPTY_TENT: TentDraft = { id: null, offerId: '', label: '', placement: 'table_tent', isActive: true };

type GroupDraft = {
  id: string | null;
  name: string;
  note: string;
  members: { id: string; name: string }[];
};

const EMPTY_GROUP: GroupDraft = { id: null, name: '', note: '', members: [] };

type Tab = 'offers' | 'placements' | 'affiliations';

/**
 * Evan 2026-09-22: *"put partnerships into venue portal. as a tab in venue portal."*
 * `embedded` is how it lives as a tab of `VenueOutreachScreen` — it drops the page chrome (its own
 * `PageHeader` and page wrapper) so it sits inside that screen's header and tab strip instead of
 * stacking a second title under the first. Everything below the header is identical either way.
 */
export function PartnershipsScreen({ embedded = false }: { embedded?: boolean }) {
  const [tab, setTab] = useState<Tab>('offers');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<OfferRow[]>([]);
  const [tents, setTents] = useState<TentRow[]>([]);
  const [groups, setGroups] = useState<AffiliationGroup[]>([]);

  const [offerDraft, setOfferDraft] = useState<OfferDraft | null>(null);
  const [tentDraft, setTentDraft] = useState<TentDraft | null>(null);
  const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [tentFilter, setTentFilter] = useState<string>('all');
  const [qrTent, setQrTent] = useState<TentRow | null>(null);

  const { showError, showSuccess, toastNode } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [offerRes, tentRes] = await Promise.all([
        /* `…_full` and not `admin_list_partner_offers`: the older TABLE-returning function cannot
           carry `status` / `proposed_by` / the review fields without a Tier-3 DROP. The jsonb one
           is the shape that grows. */
        supabase.rpc('admin_list_partner_offers_full'),
        supabase.rpc('admin_partner_tent_stats', { p_offer_id: null }),
      ]);
      if (offerRes.error) throw offerRes.error;
      if (tentRes.error) throw tentRes.error;
      setOffers((offerRes.data ?? []) as OfferRow[]);
      setTents((tentRes.data ?? []) as TentRow[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load partnerships');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Affiliations are a plain table read — there is no admin list RPC for them, and both tables
   * grant `authenticated` a `using (true)` select. Venue names come from a second query rather
   * than a PostgREST embed so a change to the FK naming can never silently empty the list.
   */
  const loadGroups = useCallback(async () => {
    try {
      const [groupRes, memberRes] = await Promise.all([
        supabase
          .from('venue_affiliation_groups')
          .select('id, name, note, created_at')
          .order('created_at', { ascending: false }),
        supabase.from('venue_affiliation_members').select('group_id, venue_id'),
      ]);
      if (groupRes.error) throw groupRes.error;
      if (memberRes.error) throw memberRes.error;

      const memberRows = (memberRes.data ?? []) as { group_id: string; venue_id: string }[];
      const venueIds = Array.from(new Set(memberRows.map((m) => m.venue_id)));
      const nameById = new Map<string, string>();
      if (venueIds.length > 0) {
        const { data: venueRows } = await supabase
          .from('venues')
          .select('id, venue_name_user_input, osm_name')
          .in('id', venueIds);
        ((venueRows ?? []) as Pick<GrowthVenue, 'id' | 'venue_name_user_input' | 'osm_name'>[]).forEach((v) =>
          nameById.set(v.id, venueDisplayName(v)),
        );
      }

      const byGroup = new Map<string, { id: string; name: string }[]>();
      memberRows.forEach((m) => {
        const list = byGroup.get(m.group_id) ?? [];
        list.push({ id: m.venue_id, name: nameById.get(m.venue_id) ?? 'Unknown venue' });
        byGroup.set(m.group_id, list);
      });

      setGroups(
        ((groupRes.data ?? []) as Omit<AffiliationGroup, 'members'>[]).map((g) => ({
          ...g,
          members: (byGroup.get(g.id) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
        })),
      );
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load affiliation groups');
    }
  }, [showError]);

  useEffect(() => {
    void load();
    void loadGroups();
  }, [load, loadGroups]);

  const pending = useMemo(() => offers.filter((o) => o.status === 'pending'), [offers]);
  const reviewed = useMemo(() => offers.filter((o) => o.status !== 'pending'), [offers]);

  const totals = useMemo(() => {
    let scans = 0;
    let claims = 0;
    let burned = 0;
    let live = 0;
    let scannerOffers = 0;
    offers.forEach((o) => {
      scans += o.tent_scans;
      claims += o.reveals;
      burned += o.redemptions;
      if (offerBadge(o).label === 'Live') live += 1;
      if (o.scan_verification_enabled) scannerOffers += 1;
    });
    return { scans, claims, burned, live, scannerOffers };
  }, [offers]);

  const offerById = useMemo(() => {
    const m = new Map<string, OfferRow>();
    offers.forEach((o) => m.set(o.id, o));
    return m;
  }, [offers]);

  const visibleTents = useMemo(() => {
    if (tentFilter === 'all') return tents;
    const offer = offerById.get(tentFilter);
    if (!offer) return [];
    // `admin_partner_tent_stats` does not return `offer_id`, so the offer's venue is the only join
    // key available client-side. A venue running two offers at once would over-match; accepted for
    // v1 because the alternative is a second round-trip per pill press.
    return tents.filter((t) => t.venue_id === offer.venue_id);
  }, [tents, tentFilter, offerById]);

  /* ---------------------------------------------------------------- offers */

  async function saveOffer() {
    if (!offerDraft) return;
    if (!offerDraft.venueId) {
      showError('Pick a venue first.');
      return;
    }
    if (!offerDraft.title.trim()) {
      showError('An offer needs a title.');
      return;
    }
    const cap = offerDraft.maxRedemptions.trim();
    const capValue = cap ? Number(cap) : null;
    if (capValue != null && (!Number.isFinite(capValue) || capValue < 1)) {
      showError('The cap must be a whole number of 1 or more, or blank for no cap.');
      return;
    }
    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_upsert_partner_offer', {
        p_id: offerDraft.id,
        p_venue_id: offerDraft.venueId,
        p_title: offerDraft.title.trim(),
        /* '' not null — the server coalesces null back to the stored value, so null can never
           clear a detail/terms line that is already there. An empty string can. */
        p_detail: offerDraft.detail,
        p_terms: offerDraft.terms,
        /* starts/ends/cap are assigned unconditionally server-side (no coalesce), so the full
           intended window must ride on every save or an edit silently wipes it. */
        p_starts_at: fromLocalInput(offerDraft.startsAt),
        p_ends_at: fromLocalInput(offerDraft.endsAt),
        /* A non-approved offer can never be published from here — `is_active` is meaningless
           while `status <> 'approved'`, and only the review step may change that. */
        p_is_active: offerDraft.status === 'approved' ? offerDraft.isActive : false,
        p_max_redemptions: capValue,
      });
      if (rpcErr) throw rpcErr;
      showSuccess(offerDraft.id ? 'Offer saved.' : 'Offer created.');
      setOfferDraft(null);
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not save the offer');
    } finally {
      setSaving(false);
    }
  }

  async function toggleOfferActive(o: OfferRow) {
    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_upsert_partner_offer', {
        p_id: o.id,
        p_venue_id: o.venue_id,
        p_title: o.title,
        p_detail: o.detail ?? '',
        p_terms: o.terms ?? '',
        p_starts_at: o.starts_at,
        p_ends_at: o.ends_at,
        p_is_active: !o.is_active,
        p_max_redemptions: o.max_redemptions,
      });
      if (rpcErr) throw rpcErr;
      showSuccess(o.is_active ? 'Offer paused — readers can no longer open it.' : 'Offer is live.');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not change the offer');
    } finally {
      setSaving(false);
    }
  }

  /** ⚠️ `approve` and `activate` are deliberately separate — approving accepts the wording, it
   *  does not publish. Rejecting always leaves the offer unpublished. */
  async function reviewOffer(o: OfferRow, approve: boolean, activate: boolean) {
    setSaving(true);
    try {
      const note = (reviewNotes[o.id] ?? '').trim();
      const { error: rpcErr } = await supabase.rpc('admin_review_partner_offer', {
        p_offer_id: o.id,
        p_approve: approve,
        p_note: note ? note : null,
        p_activate: activate,
      });
      if (rpcErr) throw rpcErr;
      setReviewNotes((n) => {
        const next = { ...n };
        delete next[o.id];
        return next;
      });
      showSuccess(
        approve
          ? activate
            ? 'Approved and published — readers can open it now.'
            : 'Approved. It is not live yet — publish it when you want it running.'
          : 'Rejected. The venue keeps the note; nothing is published.',
      );
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not record the decision');
    } finally {
      setSaving(false);
    }
  }

  /* ------------------------------------------------------------ placements */

  async function saveTent() {
    if (!tentDraft) return;
    const offer = offerById.get(tentDraft.offerId);
    if (!offer) {
      showError('Pick the offer this placement points at.');
      return;
    }
    if (!tentDraft.label.trim()) {
      showError('Give the placement a label — it is how you tell two tents apart.');
      return;
    }
    setSaving(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_upsert_partner_tent', {
        p_id: tentDraft.id,
        p_venue_id: offer.venue_id,
        p_offer_id: offer.id,
        p_label: tentDraft.label.trim(),
        p_placement: tentDraft.placement,
        p_is_active: tentDraft.isActive,
      });
      if (rpcErr) throw rpcErr;
      const row = Array.isArray(data) ? (data[0] as { id: string; slug: string } | undefined) : undefined;
      showSuccess(tentDraft.id ? 'Placement saved.' : `Placement created — code ${row?.slug ?? ''}`);
      setTentDraft(null);
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not save the placement');
    } finally {
      setSaving(false);
    }
  }

  /* ---------------------------------------------------------- affiliations */

  async function saveGroup() {
    if (!groupDraft) return;
    if (!groupDraft.name.trim()) {
      showError('Name the group.');
      return;
    }
    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_set_venue_affiliation', {
        p_group_id: groupDraft.id,
        p_name: groupDraft.name.trim(),
        p_note: groupDraft.note,
        /* Membership is REPLACED wholesale for this group on every call — the full intended list
           must be sent, never a delta. */
        p_venue_ids: groupDraft.members.map((m) => m.id),
      });
      if (rpcErr) throw rpcErr;
      showSuccess(groupDraft.id ? 'Group saved.' : 'Group created.');
      setGroupDraft(null);
      await loadGroups();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not save the group');
    } finally {
      setSaving(false);
    }
  }

  function startEditing(o: OfferRow) {
    setOfferDraft({
      id: o.id,
      status: o.status,
      venueId: o.venue_id,
      venueName: o.venue_name,
      title: o.title,
      detail: o.detail ?? '',
      terms: o.terms ?? '',
      startsAt: toLocalInput(o.starts_at),
      endsAt: toLocalInput(o.ends_at),
      isActive: o.is_active,
      maxRedemptions: o.max_redemptions == null ? '' : String(o.max_redemptions),
    });
  }

  const reviewControls = (o: OfferRow) => (
    <div className="mt-4 border-t border-border/70 pt-3.5">
      <Field
        label="Note to file"
        htmlFor={`review-note-${o.id}`}
        hint="Optional. The venue sees it in its portal alongside the decision."
      >
        <input
          id={`review-note-${o.id}`}
          className="admin-input"
          value={reviewNotes[o.id] ?? ''}
          placeholder={o.status === 'rejected' ? 'Why it did not run…' : 'Anything the venue should know…'}
          onChange={(e) => setReviewNotes((n) => ({ ...n, [o.id]: e.target.value }))}
        />
      </Field>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="admin-btn admin-btn-primary admin-btn-sm"
          disabled={saving}
          onClick={() => void reviewOffer(o, true, true)}
        >
          Approve &amp; publish
        </button>
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-btn-sm"
          disabled={saving}
          onClick={() => void reviewOffer(o, true, false)}
        >
          Approve only
        </button>
        <button
          type="button"
          className="admin-btn admin-btn-danger admin-btn-sm"
          disabled={saving}
          onClick={() => void reviewOffer(o, false, false)}
        >
          Reject
        </button>
        <span className="text-[11px] leading-relaxed text-mutedDim">
          Approving accepts the wording. It does not publish — that is the second button&apos;s job, and a decision
          here can be changed later.
        </span>
      </div>
    </div>
  );

  const offerBody = (o: OfferRow) => (
    <>
      {o.detail ? <p className="mt-2 max-w-xl text-[12.5px] leading-relaxed text-textPrimary">{o.detail}</p> : null}
      {o.terms ? <p className="mt-1 max-w-xl text-[11.5px] leading-relaxed text-mutedDim">{o.terms}</p> : null}
    </>
  );

  /* ----------------------------------------------------------------- views */

  const offersView = (
    <div className="space-y-6">
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        <StatCard label="Live offers" value={totals.live} sub={`${offers.length} total`} />
        <StatCard label="Tent scans" value={totals.scans} sub="a phone opened a placement" />
        <StatCard
          label="Claimed"
          value={totals.claims}
          sub={`${pct(totals.claims, totals.scans)} of scans — once ever, per person`}
        />
        <StatCard
          label="Staff-burned"
          value={totals.scannerOffers === 0 ? null : totals.burned}
          sub={
            totals.scannerOffers === 0
              ? 'no offer has staff scanning on'
              : `${totals.scannerOffers} offer${totals.scannerOffers === 1 ? '' : 's'} with a scanner`
          }
        />
      </div>

      {/* REVIEW QUEUE — the only door a venue's proposal can come through. Always first. */}
      {pending.length > 0 ? (
        <section className="space-y-3">
          <div className="flex items-baseline gap-2.5">
            <span className="admin-eyebrow">Waiting on you</span>
            <span className="admin-status admin-status-warning">{pending.length}</span>
          </div>
          <p className="max-w-2xl text-[12px] leading-relaxed text-mutedDim">
            Proposed by a venue from its own portal. It is invisible to every reader until you approve it, and
            invisible again until you publish it — the two are separate on purpose.
          </p>
          {pending.map((o) => (
            <section key={o.id} className="admin-card border-warning/35">
              <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="admin-label-primary truncate">{o.title}</span>
                    <span className="admin-status admin-status-warning">Pending review</span>
                  </div>
                  <div className="mt-1.5 text-[12px] text-mutedDim">
                    {o.venue_name ?? 'Unknown venue'} · {formatWindow(o)} · proposed{' '}
                    {o.proposed_by_username ? `by ${o.proposed_by_username} ` : ''}
                    {timeAgo(o.created_at)}
                  </div>
                  {offerBody(o)}
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
                  onClick={() => startEditing(o)}
                >
                  Edit wording
                </button>
              </div>
              {reviewControls(o)}
            </section>
          ))}
        </section>
      ) : null}

      {offerDraft ? (
        <section className="admin-card space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="admin-eyebrow">{offerDraft.id ? 'Edit offer' : 'New offer'}</div>
            <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => setOfferDraft(null)}>
              Cancel
            </button>
          </div>

          <div>
            <VenuePicker
              current={offerDraft.venueId ? venueStub(offerDraft.venueId, offerDraft.venueName) : null}
              onPick={(v) =>
                setOfferDraft((d) => (d ? { ...d, venueId: v.id, venueName: venueDisplayName(v) } : d))
              }
            />
            <p className="mt-1.5 text-[11px] leading-relaxed text-mutedDim">
              The picker searches venues that already have at least one Shado rating. A partner with no ratings yet
              will not appear.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" htmlFor="offer-title" hint="What the reader sees first. Keep it to the deal itself.">
              <input
                id="offer-title"
                className="admin-input"
                value={offerDraft.title}
                placeholder="$2 off any pitcher"
                onChange={(e) => setOfferDraft((d) => (d ? { ...d, title: e.target.value } : d))}
              />
            </Field>
            <Field label="Cap" htmlFor="offer-cap" hint="Total claims allowed across everyone. Blank for no cap.">
              <input
                id="offer-cap"
                className="admin-input"
                inputMode="numeric"
                value={offerDraft.maxRedemptions}
                placeholder="No cap"
                onChange={(e) => setOfferDraft((d) => (d ? { ...d, maxRedemptions: e.target.value } : d))}
              />
            </Field>
          </div>

          <Field label="Detail" htmlFor="offer-detail" hint="One line under the title — how to use it.">
            <textarea
              id="offer-detail"
              rows={2}
              className="admin-input !h-auto py-2 leading-relaxed"
              value={offerDraft.detail}
              placeholder="Show this screen to your server before you order."
              onChange={(e) => setOfferDraft((d) => (d ? { ...d, detail: e.target.value } : d))}
            />
          </Field>

          <Field label="Terms" htmlFor="offer-terms" hint="The small print the venue will hold you to.">
            <textarea
              id="offer-terms"
              rows={2}
              className="admin-input !h-auto py-2 leading-relaxed"
              value={offerDraft.terms}
              placeholder="One per person, ever. 21+. Not valid with other offers."
              onChange={(e) => setOfferDraft((d) => (d ? { ...d, terms: e.target.value } : d))}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Starts" htmlFor="offer-starts" hint="Blank means it is open from the moment it goes live.">
              <input
                id="offer-starts"
                type="datetime-local"
                className="admin-input"
                value={offerDraft.startsAt}
                onChange={(e) => setOfferDraft((d) => (d ? { ...d, startsAt: e.target.value } : d))}
              />
            </Field>
            <Field label="Ends" htmlFor="offer-ends" hint="Blank means it runs until you pause it.">
              <input
                id="offer-ends"
                type="datetime-local"
                className="admin-input"
                value={offerDraft.endsAt}
                onChange={(e) => setOfferDraft((d) => (d ? { ...d, endsAt: e.target.value } : d))}
              />
            </Field>
          </div>

          {offerDraft.status === 'approved' ? (
            <label className="flex w-fit cursor-pointer items-center gap-2.5 text-[13px] text-textPrimary select-none">
              <input
                type="checkbox"
                checked={offerDraft.isActive}
                onChange={(e) => setOfferDraft((d) => (d ? { ...d, isActive: e.target.checked } : d))}
              />
              Live — readers can open this offer
            </label>
          ) : (
            <p className="text-[12px] leading-relaxed text-mutedDim">
              This offer is <span className="text-textPrimary">{offerDraft.status}</span>, so it cannot be published
              from here. Fix the wording, save, then decide in the review block above.
            </p>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              className="admin-btn admin-btn-primary admin-btn-sm"
              disabled={saving}
              onClick={() => void saveOffer()}
            >
              {saving ? 'Saving…' : offerDraft.id ? 'Save offer' : 'Create offer'}
            </button>
            <span className="text-[11px] text-mutedDim">
              Staff scanning is not set here — the venue turns it on from its own portal.
            </span>
          </div>
        </section>
      ) : null}

      {reviewed.length === 0 ? (
        pending.length === 0 ? (
          <EmptyState
            title="No offers yet"
            hint="An offer is the deal a reader sees after scanning a table tent. You author it here; a venue can also propose one from its portal for you to approve."
            action={
              <button
                type="button"
                className="admin-btn admin-btn-primary admin-btn-sm"
                onClick={() => setOfferDraft(EMPTY_OFFER)}
              >
                New offer
              </button>
            }
          />
        ) : null
      ) : (
        <div className="space-y-3">
          {reviewed.map((o) => {
            const badge = offerBadge(o);
            const scanOn = o.scan_verification_enabled;
            return (
              <section key={o.id} className="admin-card admin-card-hover">
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="admin-label-primary truncate">{o.title}</span>
                      <span className={`admin-status admin-status-${badge.tone}`}>{badge.label}</span>
                      <span className={`admin-status ${scanOn ? 'admin-status-accent' : 'admin-status-neutral'}`}>
                        {scanOn ? 'Staff scan on' : 'No scanner'}
                      </span>
                      {/**
                       * ⚠️ WHAT YOU HAVE TO DO TO EARN IT (Evan 2026-09-28). Unlike the scan pill
                       * beside it — which the VENUE owns from its own portal — this one is Evan's,
                       * so it is a control and not a read-out.
                       *
                       * "Rate it first" is the option that makes a discount buy something: a tent
                       * on a table turns a walk-in into a rating. "Anyone" is the broadcast case,
                       * and ⚠️ the broadcast itself does not exist yet — choosing it only makes the
                       * offer earnable by anyone signed in, it does not send anything.
                       */}
                      <select
                        className="admin-input admin-input-sm"
                        value={o.earn_condition ?? 'none'}
                        onChange={(e) => {
                          const next = e.target.value;
                          void (async () => {
                            try {
                              const { error } = await supabase.rpc(
                                'admin_set_partner_offer_earn_condition',
                                { p_offer_id: o.id, p_earn_condition: next },
                              );
                              if (error) throw error;
                              await load();
                              showSuccess(
                                next === 'first_rating'
                                  ? 'They must rate it first.'
                                  : 'Anyone signed in can take it.',
                              );
                            } catch (err) {
                              showError(
                                err instanceof Error ? err.message : 'Could not change the condition',
                              );
                            }
                          })();
                        }}
                      >
                        <option value="first_rating">Must rate it first</option>
                        <option value="none">Anyone can take it</option>
                      </select>
                    </div>
                    <div className="mt-1.5 text-[12px] text-mutedDim">
                      {o.venue_name ?? 'Unknown venue'} · {formatWindow(o)}
                      {o.max_redemptions != null ? ` · cap ${o.max_redemptions.toLocaleString()}` : ' · no cap'}
                      {o.proposed_by_username ? ` · proposed by ${o.proposed_by_username}` : ''}
                    </div>
                    {offerBody(o)}
                    {o.review_note ? (
                      <p className="mt-2 max-w-xl text-[11.5px] leading-relaxed text-mutedDim">
                        <span className="admin-eyebrow">Your note</span> — {o.review_note}
                        {o.reviewed_at ? ` (${timeAgo(o.reviewed_at)})` : ''}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      disabled={saving || o.status !== 'approved'}
                      title={o.status !== 'approved' ? 'Only an approved offer can be published' : undefined}
                      onClick={() => void toggleOfferActive(o)}
                    >
                      {o.is_active ? 'Pause' : 'Publish'}
                    </button>
                    <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => startEditing(o)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => {
                        setTentFilter(o.id);
                        setTab('placements');
                      }}
                    >
                      Placements ({o.placements})
                    </button>
                  </div>
                </div>

                {/* Funnel. Two steps when nothing can burn the code, three when something can. */}
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/70 pt-3.5">
                  <FunnelStep label="Tent scans" value={o.tent_scans} />
                  <FunnelArrow rate={pct(o.reveals, o.tent_scans)} />
                  <FunnelStep label={scanOn ? 'Reveals' : 'Claimed'} value={o.reveals} />
                  {scanOn ? (
                    <>
                      <FunnelArrow rate={pct(o.redemptions, o.reveals)} />
                      <FunnelStep label="Burned by staff" value={o.redemptions} />
                    </>
                  ) : null}
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-mutedDim">
                  {scanOn
                    ? 'Staff scanning is on for this offer, so a reveal and a redemption are two different events: the reader opens the code, then a member of staff burns it at the /scan page.'
                    : 'No staff scanner on this offer, so the reveal IS the redemption — a reader can only ever open it once. Nothing sets a redeemed timestamp in this mode, which is why there is no third number.'}
                </p>

                {o.status === 'rejected' ? reviewControls(o) : null}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );

  const placementsView = (
    <div className="space-y-6">
      <div className="admin-card">
        <div className="admin-eyebrow mb-2">Before you print anything</div>
        <p className="max-w-2xl text-[12.5px] leading-relaxed text-textPrimary">
          Every placement carries its own code so the numbers can say <em>which tent works</em>, not merely that the
          offer works. Two tents on the same offer are the experiment.
        </p>
        <p className="mt-2 max-w-2xl text-[12px] leading-relaxed text-mutedDim">
          ⚠️ The QR below encodes <span className="admin-chip-id">{TENT_URL_BASE}&lt;code&gt;</span>, and nothing serves
          that address yet — the reader-side scan-and-reveal flow is not built. The codes themselves are real and
          permanent; the destination is not. A placement&apos;s code is issued once and never changes, because it ends
          up printed on card stock sitting on a table.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="admin-pill" data-active={tentFilter === 'all'} onClick={() => setTentFilter('all')}>
          All placements <span className="text-mutedDim">{tents.length}</span>
        </button>
        {offers.map((o) => (
          <button
            key={o.id}
            type="button"
            className="admin-pill"
            data-active={tentFilter === o.id}
            onClick={() => setTentFilter(o.id)}
          >
            {o.title} <span className="text-mutedDim">{o.placements}</span>
          </button>
        ))}
        <button
          type="button"
          className="admin-btn admin-btn-primary admin-btn-sm ml-auto"
          disabled={offers.length === 0}
          title={offers.length === 0 ? 'Author an offer first — a placement points at one' : undefined}
          onClick={() => setTentDraft({ ...EMPTY_TENT, offerId: tentFilter === 'all' ? offers[0]?.id ?? '' : tentFilter })}
        >
          New placement
        </button>
      </div>

      {tentDraft ? (
        <section className="admin-card space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="admin-eyebrow">{tentDraft.id ? 'Edit placement' : 'New placement'}</div>
            <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => setTentDraft(null)}>
              Cancel
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Offer" htmlFor="tent-offer" hint="The placement inherits this offer's venue.">
              <select
                id="tent-offer"
                className="admin-input"
                value={tentDraft.offerId}
                onChange={(e) => setTentDraft((d) => (d ? { ...d, offerId: e.target.value } : d))}
              >
                <option value="">Pick an offer…</option>
                {offers.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title} — {o.venue_name ?? 'Unknown venue'}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Label" htmlFor="tent-label" hint="Where it physically sits: Booth 4, Bar right, Front window.">
              <input
                id="tent-label"
                className="admin-input"
                value={tentDraft.label}
                placeholder="Booth 4"
                onChange={(e) => setTentDraft((d) => (d ? { ...d, label: e.target.value } : d))}
              />
            </Field>
            <Field label="Kind" htmlFor="tent-kind">
              <select
                id="tent-kind"
                className="admin-input"
                value={tentDraft.placement}
                onChange={(e) => setTentDraft((d) => (d ? { ...d, placement: e.target.value } : d))}
              >
                {PLACEMENT_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <label className="flex w-fit cursor-pointer items-center gap-2.5 text-[13px] text-textPrimary select-none">
            <input
              type="checkbox"
              checked={tentDraft.isActive}
              onChange={(e) => setTentDraft((d) => (d ? { ...d, isActive: e.target.checked } : d))}
            />
            Active — this code opens the offer
          </label>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              className="admin-btn admin-btn-primary admin-btn-sm"
              disabled={saving}
              onClick={() => void saveTent()}
            >
              {saving ? 'Saving…' : tentDraft.id ? 'Save placement' : 'Create placement'}
            </button>
            <span className="text-[11px] text-mutedDim">
              {tentDraft.id
                ? 'The code cannot be changed — tents already in the wild would stop working.'
                : 'A permanent code is issued on save.'}
            </span>
          </div>
        </section>
      ) : null}

      {visibleTents.length === 0 ? (
        <EmptyState
          title={tents.length === 0 ? 'No placements yet' : 'No placements for this offer'}
          hint="A placement is one physical thing in the room — a tent on a table, a poster by the door. Each gets its own code so you can tell which one earns its space."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Placement</th>
                <th>Venue</th>
                <th>Code</th>
                <th className="num">Scans</th>
                <th className="num" title="Reveal opens, including a reader re-opening their own code">
                  Opens
                </th>
                <th className="num" title="Only ever non-zero when the venue has staff scanning on">
                  Burned
                </th>
                <th>Last scan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleTents.map((t) => (
                <tr key={t.tent_id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="text-textPrimary">{t.label}</span>
                      {t.is_active ? null : <span className="admin-status admin-status-neutral">Off</span>}
                    </div>
                    <div className="text-[11px] text-mutedDim">{placementLabel(t.placement)}</div>
                  </td>
                  <td className="text-mutedDim">{t.venue_name ?? '—'}</td>
                  <td>
                    <span className="admin-chip-id">{t.slug}</span>
                  </td>
                  <td className="num">{t.scans.toLocaleString()}</td>
                  <td className="num">{t.reveals.toLocaleString()}</td>
                  <td className="num">{t.redemptions.toLocaleString()}</td>
                  <td className="text-mutedDim">{t.last_scan_at ? timeAgo(t.last_scan_at) : '—'}</td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setQrTent(t)}>
                        QR
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => {
                          const offer = offers.find((o) => o.venue_id === t.venue_id);
                          setTentDraft({
                            id: t.tent_id,
                            offerId: offer?.id ?? '',
                            label: t.label,
                            placement: t.placement,
                            isActive: t.is_active,
                          });
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 max-w-2xl text-[11px] leading-relaxed text-mutedDim">
            <span className="font-semibold">Scans</span> is a phone opening this placement.{' '}
            <span className="font-semibold">Opens</span> counts reveal events, so one reader re-opening their own code
            adds to it — it is not a headcount, unlike the per-offer number.{' '}
            <span className="font-semibold">Burned</span> only moves on offers where the venue has staff scanning on.
          </p>
        </div>
      )}
    </div>
  );

  const affiliationsView = (
    <div className="space-y-6">
      <div className="admin-card">
        <div className="admin-eyebrow mb-2">What this is for</div>
        <p className="max-w-2xl text-[12.5px] leading-relaxed text-textPrimary">
          A quiet marker that some venues belong together — one ownership group, one promo collective. A venue can sit
          in more than one group.
        </p>
        <p className="mt-2 max-w-2xl text-[12px] leading-relaxed text-mutedDim">
          Saving replaces the group&apos;s whole membership, so the list you see below is the list that gets written.
          Groups cannot be deleted from here; empty one out instead.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setGroupDraft(EMPTY_GROUP)}>
          New group
        </button>
      </div>

      {groupDraft ? (
        <section className="admin-card space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="admin-eyebrow">{groupDraft.id ? 'Edit group' : 'New group'}</div>
            <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => setGroupDraft(null)}>
              Cancel
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="group-name">
              <input
                id="group-name"
                className="admin-input"
                value={groupDraft.name}
                placeholder="Ann Arbor — Jug group"
                onChange={(e) => setGroupDraft((d) => (d ? { ...d, name: e.target.value } : d))}
              />
            </Field>
            <Field label="Note" htmlFor="group-note" hint="Only you see this.">
              <input
                id="group-note"
                className="admin-input"
                value={groupDraft.note}
                placeholder="Same owner, one contact for both"
                onChange={(e) => setGroupDraft((d) => (d ? { ...d, note: e.target.value } : d))}
              />
            </Field>
          </div>
          <div>
            <VenuePicker
              current={null}
              onPick={(v) =>
                setGroupDraft((d) => {
                  if (!d) return d;
                  if (d.members.some((m) => m.id === v.id)) return d;
                  return { ...d, members: [...d.members, { id: v.id, name: venueDisplayName(v) }] };
                })
              }
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {groupDraft.members.length === 0 ? (
                <span className="text-[12px] text-mutedDim">No venues in this group yet.</span>
              ) : (
                groupDraft.members.map((m) => (
                  <span key={m.id} className="admin-chip-id !text-[12px] !text-textPrimary">
                    {m.name}
                    <button
                      type="button"
                      aria-label={`Remove ${m.name}`}
                      className="text-mutedDim hover:text-textPrimary"
                      onClick={() =>
                        setGroupDraft((d) => (d ? { ...d, members: d.members.filter((x) => x.id !== m.id) } : d))
                      }
                    >
                      ✕
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-primary admin-btn-sm"
            disabled={saving}
            onClick={() => void saveGroup()}
          >
            {saving ? 'Saving…' : groupDraft.id ? 'Save group' : 'Create group'}
          </button>
        </section>
      ) : null}

      {groups.length === 0 ? (
        <EmptyState title="No affiliation groups yet" hint="Group venues that share an owner, a manager, or a promoter." />
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <section key={g.id} className="admin-card admin-card-hover">
              <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
                <div className="min-w-0">
                  <div className="admin-label-primary">{g.name}</div>
                  {g.note ? <div className="mt-1 text-[12px] text-mutedDim">{g.note}</div> : null}
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
                  onClick={() => setGroupDraft({ id: g.id, name: g.name, note: g.note ?? '', members: g.members })}
                >
                  Edit
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {g.members.length === 0 ? (
                  <span className="text-[12px] text-mutedDim">No venues in this group.</span>
                ) : (
                  g.members.map((m) => (
                    <span key={m.id} className="admin-chip-id !text-[12px]">
                      {m.name}
                    </span>
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {toastNode}
      <TentQrModal tent={qrTent} onClose={() => setQrTent(null)} onCopied={showSuccess} onCopyFailed={showError} />
      <div className={embedded ? '' : 'admin-page max-w-5xl mx-auto'}>
        {embedded ? (
          <div className="mb-6 flex items-start justify-between gap-4">
            <p className="max-w-2xl text-[13px] text-mutedDim">
              Offers you author or approve, the table tents that carry them, and which venues belong
              together. A venue can propose a deal; only you publish one.
            </p>
            <div className="flex shrink-0 gap-2"><>
              {tab === 'offers' ? (
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  onClick={() => setOfferDraft(EMPTY_OFFER)}
                >
                  New offer
                </button>
              ) : null}
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                disabled={loading}
                onClick={() => {
                  void load();
                  void loadGroups();
                }}
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </></div>
          </div>
        ) : (
          <PageHeader
            title="Partnerships"
            subtitle="Offers you author or approve, the table tents that carry them, and which venues belong together. A venue can propose a deal; only you publish one."
            actions={<>
              {tab === 'offers' ? (
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  onClick={() => setOfferDraft(EMPTY_OFFER)}
                >
                  New offer
                </button>
              ) : null}
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                disabled={loading}
                onClick={() => {
                  void load();
                  void loadGroups();
                }}
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </>}
          />
        )}

        {error ? <ErrorBanner className="mb-6">{error}</ErrorBanner> : null}

        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button type="button" className="admin-pill" data-active={tab === 'offers'} onClick={() => setTab('offers')}>
            Offers <span className="text-mutedDim">{offers.length}</span>
            {pending.length > 0 ? <span className="ml-1.5 text-warning">{pending.length} waiting</span> : null}
          </button>
          <button
            type="button"
            className="admin-pill"
            data-active={tab === 'placements'}
            onClick={() => setTab('placements')}
          >
            Placements <span className="text-mutedDim">{tents.length}</span>
          </button>
          <button
            type="button"
            className="admin-pill"
            data-active={tab === 'affiliations'}
            onClick={() => setTab('affiliations')}
          >
            Affiliations <span className="text-mutedDim">{groups.length}</span>
          </button>
        </div>

        {loading ? (
          <SkeletonRows rows={6} className="py-4" />
        ) : tab === 'offers' ? (
          offersView
        ) : tab === 'placements' ? (
          placementsView
        ) : (
          affiliationsView
        )}
      </div>
    </>
  );
}

function FunnelStep({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[96px]">
      <div className="text-[18px] font-semibold tabular-nums leading-none text-textPrimary">
        {value.toLocaleString()}
      </div>
      <div className="mt-1 text-2xs font-semibold uppercase tracking-[0.08em] text-mutedDim">{label}</div>
    </div>
  );
}

function FunnelArrow({ rate }: { rate: string }) {
  return (
    <div className="flex items-center gap-1.5 pb-3 text-mutedDim">
      <span aria-hidden>→</span>
      <span className="text-[11px] tabular-nums">{rate}</span>
    </div>
  );
}

function TentQrModal({
  tent,
  onClose,
  onCopied,
  onCopyFailed,
}: {
  tent: TentRow | null;
  onClose: () => void;
  onCopied: (m: string) => void;
  onCopyFailed: (m: string) => void;
}) {
  useEscToClose(Boolean(tent), onClose);
  if (!tent) return null;
  const url = `${TENT_URL_BASE}${tent.slug}`;
  return (
    <div className="dash-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="dash-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`QR for ${tent.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{tent.label}</h3>
        <p>
          {placementLabel(tent.placement)} · {tent.venue_name ?? 'Unknown venue'}
        </p>
        <div className="my-4 flex items-center gap-5">
          <span className="shrink-0 rounded-lg bg-white p-3">
            <QRCodeSVG value={url} size={148} fgColor="#08090A" bgColor="#FFFFFF" />
          </span>
          <div className="min-w-0">
            <div className="admin-label">Code</div>
            <div className="admin-chip-id">{tent.slug}</div>
            <div className="admin-label mt-3">Encodes</div>
            <div className="break-all text-[12px] text-mutedDim">{url}</div>
            <p className="mt-3 text-[11px] leading-relaxed text-mutedDim">
              This code is permanent. Nothing serves the address yet — the reader flow is not built.
            </p>
          </div>
        </div>
        <div className="dash-modal-actions">
          <button
            type="button"
            className="dash-btn dash-btn-ghost"
            onClick={() => {
              /* A rejected clipboard promise is the common failure (insecure origin, denied
                 permission), so it has to be caught on the promise, not around the call. */
              try {
                void navigator.clipboard
                  .writeText(url)
                  .then(() => onCopied('Link copied.'))
                  .catch(() => onCopyFailed('Could not copy — select the address instead.'));
              } catch {
                onCopyFailed('Could not copy — select the address instead.');
              }
            }}
          >
            Copy link
          </button>
          <button type="button" className="dash-btn dash-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
